import React, { useRef, useState, useEffect, useImperativeHandle } from 'react';
import './index.module.less';
import jsQR from "jsqr";
import { throttle } from 'lodash';
import { TCode } from './index.d';
import { getFitSize, getPx } from '@helpers/utils';
import Toast from '@widgets/Toast';
import ConfirmModal from '@views/components/ConfirmModal';
import tipImage from './images/device_scan_tip.png';
import ImageScan from '../ImageScan';
import FridayService from '@services/friday';

if (navigator.mediaDevices === undefined) {
  // @ts-ignore
  navigator.mediaDevices = {};
}
if (navigator.mediaDevices.getUserMedia === undefined) {
  navigator.mediaDevices.getUserMedia = function(constraints) {
    // 首先，如果有 getUserMedia 的话，就获得它
    // @ts-ignore
    var getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
    // 一些浏览器根本没实现它 - 那么就返回一个 error 到 promise 的 reject 来保持一个统一的接口
    if (!getUserMedia) {
      return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
    }
    // 否则，为老的 navigator.getUserMedia 方法包裹一个 Promise
    return new Promise(function(resolve, reject) {
      getUserMedia.call(navigator, constraints, resolve, reject);
    });
  }
}

// @ts-ignore
// const requestAnimationFrame = window.requestAnimationFrame || window.mozRequestAnimationFrame || window.webkitRequestAnimationFrame || window.msRequestAnimationFrame;
// const cancelAnimationFrame = window.cancelAnimationFrame || window.mozCancelAnimationFrame;

const requestAnimationFrame = (cb: Function) => {
  return setTimeout(cb, 33);
};
const cancelAnimationFrame = clearTimeout;

interface IQrcodeScanProps {
  layout?: { width: number, height: number };
  onScan?: (data: string) => void;
}

const QrcodeScan = React.forwardRef((props: IQrcodeScanProps, ref) => {
  const { layout, onScan } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasScanRef = useRef<HTMLCanvasElement>(null);
  const activeRef = useRef(true);
  const codeRef = useRef<TCode>(null);
  const [maskVisible, setMaskVisible] = useState(true);
  const [scanAnimationVisible, setScanAnimationVisible] = useState(false);
  const streamRef = useRef<any>(null);
  const tipTimerRef = useRef<any>(null);
  const animationFrameTimer = useRef<any>(null);

  const ContainerSize = {
    width: layout?.width || document.body.clientWidth,
    height: layout?.height || document.body.clientHeight,
  };

  const ScanSize = {
    left: 0,
    top: 0,
    width: Math.floor(getPx(560)),
    height: Math.floor(getPx(560)),
  };
  ScanSize.left = (ContainerSize.width - ScanSize.width) * 0.5;
  ScanSize.top = (ContainerSize.height - ScanSize.height) * 0.35;

  useImperativeHandle(ref, () => ({
    resume: () => {
      activeRef.current = true;
      setMaskVisible(true);
    }
  }));

  useEffect(() => {
    tipTimerRef.current = setTimeout(() => {
      handleErrorTips();
    }, 5000);
    
    // 延迟启动
    const initTimer = setTimeout(() => {
      init();
    }, 100);

    return () => {
      clearTimeout(initTimer);

      // 关闭摄像头
      const stream = streamRef.current;
      stopStream(stream);

      clearTipTimer();

      if (animationFrameTimer.current) {
        cancelAnimationFrame(animationFrameTimer.current);
        animationFrameTimer.current = null;
      }
    }
    // eslint-disable-next-line
  }, []);

  const handleErrorTips = () => {
    Toast.show({
      type: 'strip',
      title: '请开启摄像头权限，或者使用相册图片扫码识别',
      timer: 3000
    });
    clearTipTimer();
  }
  
  const init = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { exact: "environment" },
          width: ContainerSize.width * 6,
          height: ContainerSize.height * 6,
        }
      })
      .then(handleSuccess)
      .catch((error) => {
        console.log("error-captured-1", error);
        navigator.mediaDevices
          .getUserMedia({
            video: {
              facingMode: { exact: "environment" },
              width: ContainerSize.width * 4,
              height: ContainerSize.height * 4,
            }
          })
          .then(handleSuccess)
          .catch(error => {
            handleErrorTips();
            console.log("error-captured-2", error);
          });
      });
    }
  }

  const clearTipTimer = () => {
    if (tipTimerRef.current) {
      clearTimeout(tipTimerRef.current);
      tipTimerRef.current = null;
    }
  }

  const handleSuccess = (stream: any) => {
    streamRef.current = stream;
    clearTipTimer();
    if (videoRef.current) {
      if (videoRef.current.srcObject !== undefined) {
        videoRef.current.srcObject = stream;
      } else if ((window as any).videoEl.mozSrcObject !== undefined) {
        (videoRef.current as any).mozSrcObject = stream;
      } else if (window.URL.createObjectURL) {
        videoRef.current.src = window.URL.createObjectURL(stream);
      } else if (window.webkitURL) {
        videoRef.current.src = window.webkitURL.createObjectURL(stream);
      } else {
        videoRef.current.src = stream;
      }
      const playPromise = videoRef.current.play();
      playPromise.then(() => {
        draw();
        setScanAnimationVisible(true);

        // 开始扫码一段时间后仍未识别成功，弹窗提示
        tipTimerRef.current = setTimeout(() => {
          ConfirmModal.show({
            title: '无法识别二维码',
            showCancelBtn: true,
            text: '可将二维码拍照，并通过相册导入识别',
            cancelBtnText: '取 消',
            confirmBtnText: (
              <div styleName="btn-image-scan">
                打开相册
                <div styleName="wrapper-image-scan">
                  <ImageScan onScan={(data) => {
                    ConfirmModal.hide();
                    onScan(data);
                  }}/>
                </div>
              </div>
            ),
            confirmTheme: 'primary',
            onConfirm: () => {}
          });
        }, 16 * 1000);
      });
    } else {
      // 页面已销毁
      stopStream(stream);
    }
  }
 
  const draw = () => {
    // 视频处于准备阶段，并且已经加载足够的数据
    if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA && activeRef.current) {
      const canvasContext = canvasRef.current.getContext('2d');
      // 开始在画布上绘制视频
      canvasRef.current.width = ContainerSize.width;
      canvasRef.current.height = ContainerSize.height;
      // 适配视频尺寸
      const videoWidth = videoRef.current.videoWidth || videoRef.current.offsetWidth;
      const videoHeight = videoRef.current.videoHeight || videoRef.current.offsetHeight;
      const { width, height } = getFitSize(videoWidth, videoHeight, canvasRef.current.width, canvasRef.current.height, 'cover');

      canvasContext.drawImage(videoRef.current, (canvasRef.current.width - width) / 2, (canvasRef.current.height - height) / 2, width, height);
      if (canvasScanRef.current) {
        canvasScanRef.current.getContext('2d').drawImage(canvasRef.current, ScanSize.left, ScanSize.top, ScanSize.width, ScanSize.height, 0, 0, ScanSize.width, ScanSize.height);
        // 使用小框canvas扫码
        scanCode(canvasScanRef.current);
      }
    }
    // 浏览器在下次重绘前循环调用方法
    animationFrameTimer.current = requestAnimationFrame(draw);
  }

  const scanCode = throttle((canvas: HTMLCanvasElement) => {
    // getImageData() 复制画布上制定矩形的像素数据
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    let code;
    try {
      // 识别二维码
      code = jsQR(imageData.data, imageData.width, imageData.height);
    } catch (e) {
      console.error(e);
    }
    if (code) {
      foundCode(code);
    }
  }, 1200);

  const foundCode = (code: TCode) => {
    if (code && code.data) {
      // 识别检查，提高准确度
      if (codeRef.current?.data === code.data) {
        clearTipTimer();
        codeRef.current = null;
        // 暂停画面绘制
        activeRef.current = false;
        setMaskVisible(false);
        // 如果识别出二维码，绘制矩形框
        drawBox(code.location);
        // 识别成功事件处理
        onScan && onScan(code.data);
      } else {
        codeRef.current = code;
      }
    }
  }

  const stopStream = (stream: any) => {
    if (stream && stream.getTracks) {
      stream.getTracks().forEach((track: any) => {
        track && track.stop && track.stop();
      });
    }
  }

  // 画线
  const drawLine = (begin: {x: number, y: number}, end: {x: number, y: number}) => {
    const canvasContext = canvasScanRef.current.getContext('2d');
    canvasContext.beginPath();
    canvasContext.moveTo(begin.x, begin.y);
    canvasContext.lineTo(end.x, end.y);
    canvasContext.lineWidth = 2;
    canvasContext.strokeStyle = '#fff';
    canvasContext.stroke();
  }

  // 画框
  const drawBox = (location: TCode['location']) => {
    drawLine(location.topLeftCorner, location.topRightCorner);
    drawLine(location.topRightCorner, location.bottomRightCorner);
    drawLine(location.bottomRightCorner, location.bottomLeftCorner);
    drawLine(location.bottomLeftCorner, location.topLeftCorner);
  }

  const handleClickTip = () => {
    FridayService.addEvent('positioning_sweep_click_to_find_qr_code');
    ConfirmModal.show({
      title: '温馨提示',
      showCancelBtn: false,
      confirmBtnText: '返回扫码',
      confirmTheme: 'normal',
      ContentWidget: (
        <div styleName="tip-content">
          <div styleName="text">揭开学生证表面的盖子即可看见设备二维码。</div>
          <img src={tipImage} alt="" />
        </div>
      )
    });
  }

  return (
    <div styleName="container">
      <video
        ref={videoRef}
        styleName="video"
        playsInline={true}
        webkit-playsinline="true"
        x5-video-player-type="h5-page"
      />
      <canvas
        ref={canvasRef}
        width={ContainerSize.width}
        height={ContainerSize.height}
      />
     
      {
        scanAnimationVisible &&
        <div styleName="wrapper">
          <div styleName="panel-wrapper" style={{left: ScanSize.left, top: ScanSize.top}}>
            <div styleName="text">请对准电子学生证二维码</div>
            <div styleName="scan-wrapper">
              <canvas
                ref={canvasScanRef}
                width={ScanSize.width}
                height={ScanSize.height}
              />
              {
                maskVisible &&
                <div styleName="mask"></div>
              }
              <div styleName="border">
                <div styleName="border-item"></div>
                <div styleName="border-item"></div>
                <div styleName="border-item"></div>
                <div styleName="border-item"></div>
              </div>
            </div>
            <div styleName="tip" onClick={handleClickTip}>找不到二维码在哪？</div>
          </div>
        </div>
      }
    </div>
  )
})

export default QrcodeScan;
